import axios, { type AxiosError, type AxiosInstance, type AxiosResponse } from 'axios'
import { z } from 'zod'

import type { HealthData, UserData } from '@eatout/api-types'

import {
  jsonApiDocumentSchema,
  jsonApiMediaType,
  parseResource,
  pointerToField,
  type JsonApiErrorObject,
} from './json-api'

export {
  jsonApiDocumentSchema,
  jsonApiMediaType,
  parseCollection,
  parseResource,
  pointerToField,
} from './json-api'

const dependencyStatusSchema = z.enum(['up', 'down'])

export const healthAttributesSchema = z.object({
  status: z.enum(['healthy', 'degraded', 'unhealthy']),
  environment: z.string(),
  git_sha: z.string(),
  dependencies: z.object({
    postgresql: dependencyStatusSchema,
    redis: dependencyStatusSchema,
    meilisearch: dependencyStatusSchema,
  }),
})

/** @deprecated Prefer healthAttributesSchema — kept for existing imports. */
export const healthDataSchema = healthAttributesSchema

export const userAttributesSchema = z.object({
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  email_verified_at: z.string().nullable(),
  phone_verified_at: z.string().nullable(),
  timezone: z.string(),
  locale: z.string(),
  default_party_size: z.number().int().nullable(),
  marketing_consent_at: z.string().nullable(),
  last_seen_at: z.string().nullable(),
})

export const userDataSchema = userAttributesSchema.extend({
  id: z.number().int(),
}) satisfies z.ZodType<UserData>

export const phoneVerificationChallengeAttributesSchema = z.object({
  masked_phone: z.string(),
  expires_at: z.string(),
  resend_available_at: z.string(),
})

export const phoneVerificationChallengeSchema = phoneVerificationChallengeAttributesSchema.extend({
  id: z.string(),
})

export type PhoneVerificationChallenge = z.infer<typeof phoneVerificationChallengeSchema>

export type ApiErrorItem = {
  status?: string | undefined
  code?: string | undefined
  title?: string | undefined
  detail?: string | undefined
  source?: { pointer?: string | undefined; parameter?: string | undefined } | undefined
  meta?: Record<string, unknown> | undefined
}

export class ApiError extends Error {
  readonly code: string
  readonly correlationId: string | undefined
  readonly status: number | undefined
  readonly errors: ApiErrorItem[]
  readonly fieldErrors: Record<string, string[]>

  constructor(
    code: string,
    message: string,
    options?: {
      correlationId?: string | undefined
      status?: number | undefined
      errors?: ApiErrorItem[] | undefined
      fieldErrors?: Record<string, string[]> | undefined
      cause?: unknown
    },
  ) {
    super(message, options?.cause !== undefined ? { cause: options.cause } : undefined)
    this.name = 'ApiError'
    this.code = code
    this.correlationId = options?.correlationId
    this.status = options?.status
    this.errors = options?.errors ?? []
    this.fieldErrors = options?.fieldErrors ?? {}
  }

  static fromAxios(error: AxiosError): ApiError {
    const headerCorrelation =
      error.response?.headers?.['x-correlation-id'] ?? error.config?.headers?.['X-Correlation-Id']
    const correlationId = typeof headerCorrelation === 'string' ? headerCorrelation : undefined

    if (error.response) {
      const status = error.response.status
      const parsed = readJsonApiErrorPayload(error.response.data, status)
      const resolvedCorrelation = correlationId ?? parsed.correlationId

      return new ApiError(parsed.code, parsed.message, {
        ...(resolvedCorrelation !== undefined ? { correlationId: resolvedCorrelation } : {}),
        errors: parsed.errors,
        fieldErrors: parsed.fieldErrors,
        status,
        cause: error,
      })
    }

    return new ApiError('NETWORK_ERROR', error.message || 'Network request failed', {
      ...(correlationId !== undefined ? { correlationId } : {}),
      cause: error,
    })
  }
}

function fallbackMessageForStatus(status: number): string {
  switch (status) {
    case 401:
      return 'Authentication is required.'
    case 403:
      return 'You are not allowed to perform this action.'
    case 404:
      return 'The requested resource was not found.'
    case 422:
      return 'The given data was invalid.'
    case 419:
      return 'Your session expired. Refresh and try again.'
    case 429:
      return 'Too many attempts. Please try again later.'
    default:
      return 'Something went wrong. Please try again.'
  }
}

function readJsonApiErrorPayload(
  data: unknown,
  status: number,
): {
  code: string
  message: string
  correlationId: string | undefined
  errors: ApiErrorItem[]
  fieldErrors: Record<string, string[]>
} {
  const fallbackMessage = fallbackMessageForStatus(status)
  const parsed = jsonApiDocumentSchema.safeParse(data)

  if (!parsed.success || parsed.data.errors === undefined || parsed.data.errors.length === 0) {
    return {
      code: status === 401 ? 'UNAUTHENTICATED' : 'INTERNAL_ERROR',
      message: fallbackMessage,
      correlationId: undefined,
      errors: [],
      fieldErrors: {},
    }
  }

  const errors = parsed.data.errors.map((error): ApiErrorItem => ({
    ...(error.status !== undefined ? { status: error.status } : {}),
    ...(error.code !== undefined ? { code: error.code } : {}),
    ...(error.title !== undefined ? { title: error.title } : {}),
    ...(error.detail !== undefined ? { detail: error.detail } : {}),
    ...(error.source !== undefined ? { source: error.source } : {}),
    ...(error.meta !== undefined ? { meta: error.meta } : {}),
  }))

  const fieldErrors: Record<string, string[]> = {}
  for (const error of errors) {
    const pointer = error.source?.pointer
    const detail = error.detail
    if (pointer === undefined || detail === undefined || detail.length === 0) {
      continue
    }

    const field = pointerToField(pointer)
    if (field.length === 0) {
      continue
    }

    fieldErrors[field] ??= []
    fieldErrors[field].push(detail)
  }

  const primary = errors[0] as JsonApiErrorObject
  const code = typeof primary.code === 'string' ? primary.code : 'INTERNAL_ERROR'
  const message =
    typeof primary.detail === 'string' && primary.detail.length > 0
      ? primary.detail
      : fallbackMessage

  const meta = parsed.data.meta
  const correlationId = typeof meta?.correlation_id === 'string' ? meta.correlation_id : undefined

  return { code, message, correlationId, errors, fieldErrors }
}

function assertNoContent(response: AxiosResponse<unknown>): void {
  if (response.status !== 204) {
    throw new ApiError(
      'INTERNAL_ERROR',
      `Expected 204 No Content, received ${String(response.status)}`,
      {
        status: response.status,
      },
    )
  }
}

function toUserData(resource: { id: string } & z.infer<typeof userAttributesSchema>): UserData {
  return userDataSchema.parse({
    id: Number(resource.id),
    name: resource.name,
    email: resource.email,
    phone: resource.phone,
    email_verified_at: resource.email_verified_at,
    phone_verified_at: resource.phone_verified_at,
    timezone: resource.timezone,
    locale: resource.locale,
    default_party_size: resource.default_party_size,
    marketing_consent_at: resource.marketing_consent_at,
    last_seen_at: resource.last_seen_at,
  })
}

export type CreateApiClientOptions = {
  baseURL: string
  getCorrelationId?: () => string
  /** Called when the API reports an unauthenticated session (not login failures). */
  onUnauthorized?: () => void
}

function mintCorrelationId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }

  return `local-${String(Date.now())}`
}

let csrfReady = false
let csrfPromise: Promise<void> | undefined

/**
 * Fetch Sanctum's XSRF cookie before the first state-changing request.
 */
export async function ensureCsrfCookie(client: AxiosInstance = getApiClient()): Promise<void> {
  if (csrfReady) {
    return
  }

  csrfPromise ??= client.get('/sanctum/csrf-cookie').then(() => {
    csrfReady = true
  })

  await csrfPromise
}

/** Reset CSRF handshake so the next state-changing call re-fetches the cookie. */
export function resetCsrfCookieState(): void {
  csrfReady = false
  csrfPromise = undefined
}

function isStateChangingMethod(method: string | undefined): boolean {
  const normalized = (method ?? 'get').toLowerCase()

  return normalized !== 'get' && normalized !== 'head' && normalized !== 'options'
}

export function createApiClient(options: CreateApiClientOptions): AxiosInstance {
  const client = axios.create({
    baseURL: options.baseURL,
    withCredentials: true,
    withXSRFToken: true,
    headers: {
      Accept: jsonApiMediaType,
    },
  })

  client.interceptors.request.use(async (config) => {
    if (isStateChangingMethod(config.method)) {
      await ensureCsrfCookie(client)
      if (config.data !== undefined && config.headers.get('Content-Type') === undefined) {
        config.headers.set('Content-Type', 'application/json')
      }
    }

    const id = options.getCorrelationId?.() ?? mintCorrelationId()
    config.headers.set('X-Correlation-Id', id)
    config.headers.set('Accept', jsonApiMediaType)

    return config
  })

  client.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      if (axios.isAxiosError(error)) {
        const apiError = ApiError.fromAxios(error)
        const config = error.config as
          (typeof error.config & { _csrfRetried?: boolean }) | undefined

        if (
          apiError.status === 419 &&
          config !== undefined &&
          config._csrfRetried !== true &&
          isStateChangingMethod(config.method)
        ) {
          resetCsrfCookieState()
          await ensureCsrfCookie(client)
          config._csrfRetried = true

          return client.request(config)
        }

        if (apiError.status === 401 && apiError.code === 'UNAUTHENTICATED') {
          options.onUnauthorized?.()
        }

        throw apiError
      }

      throw error
    },
  )

  return client
}

let defaultClient: AxiosInstance | undefined

export function configureApiClient(options: CreateApiClientOptions): AxiosInstance {
  defaultClient = createApiClient(options)
  resetCsrfCookieState()

  return defaultClient
}

export function getApiClient(): AxiosInstance {
  if (!defaultClient) {
    throw new ApiError(
      'CLIENT_NOT_CONFIGURED',
      'API client is not configured. Call configureApiClient() at app startup.',
    )
  }

  return defaultClient
}

export function setApiClient(client: AxiosInstance): void {
  defaultClient = client
}

function rethrowAsApiError(error: unknown): never {
  if (error instanceof ApiError || error instanceof z.ZodError) {
    throw error
  }

  if (axios.isAxiosError(error)) {
    throw ApiError.fromAxios(error)
  }

  throw error
}

export async function fetchHealth(client: AxiosInstance = getApiClient()): Promise<HealthData> {
  try {
    const response = await client.get<unknown>('/api/v1/health', {
      validateStatus: (status) => status === 200 || status === 503,
    })

    const resource = parseResource(response.data, 'health_checks', healthAttributesSchema)

    return healthAttributesSchema.parse({
      status: resource.status,
      environment: resource.environment,
      git_sha: resource.git_sha,
      dependencies: resource.dependencies,
    })
  } catch (error) {
    rethrowAsApiError(error)
  }
}

export type LoginCredentials = {
  email: string
  password: string
}

export type RegisterPayload = {
  name: string
  email: string
  password: string
  phone?: string | undefined
  timezone?: string | undefined
  locale?: string | undefined
}

export async function login(
  credentials: LoginCredentials,
  client: AxiosInstance = getApiClient(),
): Promise<UserData> {
  try {
    await ensureCsrfCookie(client)
    const response = await client.post<unknown>('/login', credentials)

    return toUserData(parseResource(response.data, 'users', userAttributesSchema))
  } catch (error) {
    rethrowAsApiError(error)
  }
}

export async function register(
  payload: RegisterPayload,
  client: AxiosInstance = getApiClient(),
): Promise<void> {
  try {
    await ensureCsrfCookie(client)
    const response = await client.post<unknown>('/register', payload)
    assertNoContent(response)
  } catch (error) {
    rethrowAsApiError(error)
  }
}

export async function logout(client: AxiosInstance = getApiClient()): Promise<void> {
  try {
    await ensureCsrfCookie(client)
    const response = await client.post('/logout')
    assertNoContent(response)
  } catch (error) {
    rethrowAsApiError(error)
  }
}

export async function fetchMe(client: AxiosInstance = getApiClient()): Promise<UserData> {
  try {
    const response = await client.get<unknown>('/api/v1/me')

    return toUserData(parseResource(response.data, 'users', userAttributesSchema))
  } catch (error) {
    rethrowAsApiError(error)
  }
}

export type UpdateProfilePayload = {
  name?: string
  timezone?: string
  locale?: string
  default_party_size?: number | null
}

export async function updateMe(
  payload: UpdateProfilePayload,
  client: AxiosInstance = getApiClient(),
): Promise<UserData> {
  try {
    await ensureCsrfCookie(client)
    const response = await client.patch<unknown>('/api/v1/me', payload)

    return toUserData(parseResource(response.data, 'users', userAttributesSchema))
  } catch (error) {
    rethrowAsApiError(error)
  }
}

export async function forgotPassword(
  email: string,
  client: AxiosInstance = getApiClient(),
): Promise<void> {
  try {
    await ensureCsrfCookie(client)
    const response = await client.post<unknown>('/forgot-password', { email })
    assertNoContent(response)
  } catch (error) {
    rethrowAsApiError(error)
  }
}

export type ResetPasswordPayload = {
  email: string
  token: string
  password: string
  password_confirmation: string
}

export async function resetPassword(
  payload: ResetPasswordPayload,
  client: AxiosInstance = getApiClient(),
): Promise<void> {
  try {
    await ensureCsrfCookie(client)
    const response = await client.post<unknown>('/reset-password', payload)
    assertNoContent(response)
  } catch (error) {
    rethrowAsApiError(error)
  }
}

export async function resendEmailVerification(
  client: AxiosInstance = getApiClient(),
): Promise<void> {
  try {
    await ensureCsrfCookie(client)
    const response = await client.post<unknown>('/email/verification-notification')
    assertNoContent(response)
  } catch (error) {
    rethrowAsApiError(error)
  }
}

export async function verifyEmailFromSignedUrl(
  verifyUrl: string,
  client: AxiosInstance = getApiClient(),
): Promise<void> {
  try {
    const response = await client.get<unknown>(verifyUrl, {
      headers: { Accept: jsonApiMediaType },
    })
    assertNoContent(response)
  } catch (error) {
    rethrowAsApiError(error)
  }
}

export async function startPhoneVerification(
  phone: string,
  client: AxiosInstance = getApiClient(),
): Promise<PhoneVerificationChallenge> {
  try {
    await ensureCsrfCookie(client)
    const response = await client.post<unknown>('/api/v1/me/phone-verification-challenges', {
      phone,
    })

    return phoneVerificationChallengeSchema.parse(
      parseResource(
        response.data,
        'phone_verification_challenges',
        phoneVerificationChallengeAttributesSchema,
      ),
    )
  } catch (error) {
    rethrowAsApiError(error)
  }
}

export async function verifyPhone(
  challengeId: string,
  code: string,
  client: AxiosInstance = getApiClient(),
): Promise<UserData> {
  try {
    await ensureCsrfCookie(client)
    const response = await client.post<unknown>(
      `/api/v1/me/phone-verification-challenges/${challengeId}/verify`,
      { code },
    )

    return toUserData(parseResource(response.data, 'users', userAttributesSchema))
  } catch (error) {
    rethrowAsApiError(error)
  }
}

export type { HealthData, UserData }
